import { supabase } from './supabase'

/**
 * Images, and why none of them have a plain URL.
 *
 * Both buckets are private. A "public" Supabase bucket is public to the whole
 * internet: the path is unguessable but unauthenticated, the link never
 * expires, and it keeps working long after somebody has left the association.
 * For a cover that is a spoiler nobody can recall — the poster belongs to an
 * event that may still be behind reveal_at. For a photograph taken at a door
 * it would be indefensible.
 *
 * So what is stored in the database is the object path, and a URL is minted on
 * read with an hour to live. An hour is longer than anybody looks at a screen
 * and shorter than a link is worth forwarding.
 */

export const COVERS = 'event-covers'
export const DOOR_PHOTOS = 'door-photos'
export const EVENT_PHOTOS = 'event-photos'
export const GIMCANA_PHOTOS = 'gimcana-photos'
export const AVATARS = 'avatars'

const SIGNED_TTL_SECONDS = 3600

/** Longest edge, in pixels, of anything that goes up. */
const MAX_EDGE = 1600
const JPEG_QUALITY = 0.82

/**
 * Shrinks a photo before it leaves the phone.
 *
 * A modern phone camera produces eight to twelve megabytes a frame. Uploading
 * that over the venue's wifi is a minute of somebody standing still, it blows
 * the bucket's five-megabyte ceiling, and nothing on a 430-pixel screen can
 * tell the difference. If anything goes wrong the original is used, because a
 * slow upload beats no cover.
 *
 * I SEMPRE PASSA PEL CANVAS, encara que no calgui encongir-la. Abans hi havia
 * un camí ràpid —si ja cabia i no calia escalar, es tornava el fitxer tal com
 * havia arribat— i aquell camí s'enduia les metadades EXIF amunt: un JPEG de
 * menys de dos megues fet amb el mòbil porta la marca del model i, si la
 * càmera té la ubicació encesa, **les coordenades d'on es va fer**. Els altres
 * tres cubells ja hi passaven sempre (`encodeJpeg` per a la galeria i la
 * gimcana, el canvas de la càmera per a la porta); els avatars i les portades
 * eren els dos que se saltaven la re-codificació, i són precisament els dos on
 * la foto surt del carret i no de la càmera de l'app.
 *
 * Re-codificar el que ja cabia costa unes dècimes de segon al telèfon i una
 * mica de qualitat. Val la pena: és l'única manera que «traiem les metadades»
 * sigui una frase certa i no «gairebé sempre».
 */
/**
 * El fitxer triat no és una imatge que aquest navegador sàpiga llegir.
 *
 * El cas real és un HEIC del carret d'un iPhone en un navegador que no el
 * descodifica. Abans això queia al `catch` de `shrinkImage` i tornava el
 * fitxer ORIGINAL, que se pujava sencer —dotze megues per la wifi de la sala—
 * i el rebutjava el servidor amb un 415. Aquí es para abans de sortir del
 * telèfon, i `errorKey()` en sap dir el motiu.
 */
export class UnreadableImage extends Error {
  constructor(cause: unknown) {
    super('the browser cannot decode this file as an image', { cause })
    this.name = 'UnreadableImage'
  }
}

export async function shrinkImage(file: File): Promise<Blob> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch (cause) {
    // Únic cas que és culpa del fitxer. Tot el que ve després pot fallar per
    // motius del navegador —sense canvas, sense codificador— i aleshores
    // tornar l'original és la degradació correcta.
    throw new UnreadableImage(cause)
  }

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))

    const canvas = document.createElement('canvas')
    // `Math.max(1, …)`, com a `encodeJpeg`: un retall panoràmic de 5000×1
    // arrodoneix el costat curt a zero, i un canvas de dimensió zero fa que
    // `toBlob` torni null. Llavors sortiríem per l'`?? file` de sota amb el
    // fitxer original i les metadades a dins, que és justament el camí que es
    // va tancar traient la drecera.
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))

    const context = canvas.getContext('2d')
    if (context === null) return file
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, encodingFor(file.type), JPEG_QUALITY)
    })
    return blob ?? file
  } catch {
    return file
  }
}

/**
 * Amb què es torna a codificar, i per què no sempre JPEG.
 *
 * Treure la drecera va fer que TOT passés pel canvas, i el canvas sempre
 * escrivia JPEG. Però el selector de portada accepta PNG i WebP a posta, i el
 * JPEG no té canal alfa: un cartell exportat amb fons transparent sortia amb
 * tots els píxels transparents a negre, sense error i sense avís. La
 * previsualització no ho ensenyava perquè mostra el fitxer d'origen, no el
 * blob codificat.
 *
 * WebP i no PNG per a les que porten alfa: manté la transparència, comprimeix
 * com el JPEG —un PNG d'una foto de 1600 px se'n va a diversos megues i el
 * cubell de portades en talla a cinc— i els dos cubells que fan servir això
 * l'accepten (portades: jpeg, png i webp; avatars: jpeg i webp).
 *
 * Segueix sent una re-codificació, o sigui que les metadades marxen igual.
 */
function encodingFor(sourceType: string): string {
  return sourceType === 'image/png' || sourceType === 'image/webp' ? 'image/webp' : 'image/jpeg'
}

/**
 * Torna a codificar una imatge a JPEG, encongida si cal.
 *
 * `shrinkImage` torna el fitxer tal com va arribar quan no val la pena
 * re-codificar-lo, i per a una portada això està bé. Per a la galeria no: el
 * seu bucket només accepta JPEG i WebP, o sigui que un PNG petit passaria pel
 * camí ràpid i el rebutjaria el servidor — o pitjor, s'hi desaria amb un tipus
 * declarat que no és el que té a dins. Aquí sempre en surt un JPEG.
 */
async function encodeJpeg(source: Blob, maxEdge: number, quality: number): Promise<Blob> {
  const bitmap = await createImageBitmap(source)
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))

  const context = canvas.getContext('2d')
  if (context === null) throw new Error('no canvas')
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality)
  })
  if (blob === null) throw new Error('no jpeg')
  return blob
}

/** La foto de galeria, a mida de mirar-la i no a mida de càmera. */
export function galleryImage(file: Blob): Promise<Blob> {
  return encodeJpeg(file, MAX_EDGE, JPEG_QUALITY)
}

/**
 * La miniatura, feta al telèfon abans de pujar res.
 *
 * El sostre de la galeria no és el que ocupa sinó el que es baixa: dues-centes
 * fotos recorregudes a mida completa són trenta-sis megues cada vegada que algú
 * passa la graella, i amb miniatures en són cinc. Quatre-cents píxels és el
 * triple del que ocupa una casella de la graella en un telèfon a 3x.
 *
 * Es fa aquí i no en cap servidor perquè no n'hi ha cap: la decisió de no
 * dependre de la màquina de ningú és el que fa que això hagi de viure al
 * navegador de qui puja.
 */
const THUMB_EDGE = 400
const THUMB_QUALITY = 0.7

export function thumbnail(file: Blob): Promise<Blob> {
  return encodeJpeg(file, THUMB_EDGE, THUMB_QUALITY)
}

const EXTENSIONS: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
}

/** Uploads a cover and returns the object path to store on the event. */
export async function uploadCover(file: File, eventId: string): Promise<string> {
  const body = await shrinkImage(file)

  // The name follows what came back, not what was hoped for. Since shrinkImage
  // always re-encodes this is a JPEG in every path that worked, but it still
  // hands back the original if the canvas itself failed, and calling that .jpg
  // would store a PNG under a name that lies about it.
  const type = body.type === '' ? 'image/jpeg' : body.type
  const extension = EXTENSIONS[type] ?? 'jpg'

  // The id in the path, and a timestamp so replacing a cover never has to
  // fight a cached copy of the previous one under the same name.
  const path = `${eventId}/${String(Date.now())}.${extension}`

  const { error } = await supabase.storage
    .from(COVERS)
    .upload(path, body, { contentType: type, upsert: false })

  if (error) throw error
  return path
}

/**
 * Uploads an avatar and returns the path to store on the profile.
 *
 * `{uid}/{when}` — the id as a folder, so the storage policy can tell whose
 * face it is without consulting a table (migration 42, same shape as the door
 * photos in 34). A new object every time rather than a replacement: changing
 * your picture must not have to win a fight with a cached copy of the last one
 * under the same name.
 *
 * The bucket refuses PNG, so anything that is not already webp is called what
 * `shrinkImage` will have encoded it as.
 */
export async function uploadAvatar(body: Blob, userId: string): Promise<string> {
  const type = body.type === 'image/webp' ? 'image/webp' : 'image/jpeg'
  const extension = type === 'image/webp' ? 'webp' : 'jpg'
  const path = `${userId}/${String(Date.now())}.${extension}`

  const { error } = await supabase.storage
    .from(AVATARS)
    .upload(path, body, { contentType: type, upsert: false })

  if (error) throw error
  return path
}

/** Which half of the diptych, which is also which permission. */
export type DoorPhotoKind = 'entrada' | 'sortida'

/**
 * Uploads a door photograph and returns the path to store on the attendance.
 *
 * The path is the permission — see migration 34. `{kind}/{event}/{uid}/{when}`,
 * with the member's id as a folder rather than the filename, so a policy can
 * decide who may touch the object without consulting a table and so that
 * retaking one writes a new object instead of overwriting the old.
 *
 * The bucket refuses PNG, unlike the covers one, so the extension comes from
 * what the blob actually is and anything unexpected is called what it will be
 * encoded as.
 */
export async function uploadDoorPhoto(
  body: Blob,
  kind: DoorPhotoKind,
  eventId: string,
  userId: string,
): Promise<string> {
  const type = body.type === 'image/webp' ? 'image/webp' : 'image/jpeg'
  const extension = type === 'image/webp' ? 'webp' : 'jpg'
  const path = `${kind}/${eventId}/${userId}/${String(Date.now())}.${extension}`

  const { error } = await supabase.storage
    .from(DOOR_PHOTOS)
    .upload(path, body, { contentType: type, upsert: false })

  if (error) throw error
  return path
}

/**
 * A URL for one stored object, good for an hour.
 *
 * Returns null rather than throwing: a cover that will not load is a missing
 * picture, and the screen already knows how to draw one of those.
 */
export async function signedUrl(bucket: string, path: string | null): Promise<string | null> {
  if (path === null || path === '') return null

  // Anything already absolute was written before the bucket existed, or by
  // hand. Left alone rather than mangled into a storage path.
  if (path.startsWith('http://') || path.startsWith('https://')) return path

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, SIGNED_TTL_SECONDS)
  if (error) return null
  return data.signedUrl
}

/** The same, for a list — one round trip instead of one per row. */
export async function signedUrls(
  bucket: string,
  paths: readonly (string | null)[],
): Promise<Map<string, string>> {
  const wanted = [...new Set(paths.filter((p): p is string => p !== null && p !== ''))]
  const relative = wanted.filter((p) => !p.startsWith('http'))
  const out = new Map<string, string>()

  for (const absolute of wanted.filter((p) => p.startsWith('http'))) out.set(absolute, absolute)
  if (relative.length === 0) return out

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrls(relative, SIGNED_TTL_SECONDS)

  if (error || data === null) return out
  for (const row of data) {
    // Both fields are nullable in the response type: a batch call reports a
    // per-object error by returning nulls for that row rather than failing the
    // whole request.
    const path: unknown = row.path
    const url: unknown = row.signedUrl
    if (typeof path === 'string' && path !== '' && typeof url === 'string' && url !== '') {
      out.set(path, url)
    }
  }
  return out
}
